import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/nl";

import Navbar from "../Navbar/Navbar";
import ScaledCanvas from "./components/ScaledCanvas";
import TopbarClock from "./components/TopbarClock";
import TeamTargetHero from "./components/TeamTargetHero";
import MostSoldServices from "./components/MostSoldServices";
import MonthlyRankList from "./components/MonthlyRankList";
import YearRankList from "./components/YearRankList";

import AnimationLayer from "./animations/AnimationLayer";

import { useDashboardData } from "./hooks/useDashboardData";
import { useRankView } from "./hooks/useRankView";
import { useSessionGuard } from "./hooks/useSessionGuard";
import { useMonthRollover } from "./hooks/useMonthRollover";
import { useAnimations } from "./hooks/useAnimations";
import { useFrameWatch } from "./hooks/useRenderTier";
import { useSecretCode } from "./hooks/useSecretCode";
import { useIdleCursor } from "./hooks/useIdleCursor";
import { useScreenAwake } from "./hooks/useScreenAwake";
import { unlockLab } from "../AnimationLab/labAccess";
import { useSaleEvents } from "./hooks/useSaleEvents";
import { useHeldRanking } from "./hooks/useHeldRanking";
import { targetProgress } from "./utils/targetProgress";
import { testShortcuts } from "./constants";

import "./Dashboard.css";

// Het salesdashboard op de tv; dit bestand zet alleen de onderdelen op hun plek.
const Dashboard = () => {
  // Maand uit de URL, bijv. /dashboard/6/2025.
  const { month, year } = useParams();
  const shownMonth = dayjs(`${year}-${month}`).locale("nl");
  const monthName = shownMonth.format("MMMM");
  const previousMonthName = shownMonth.subtract(1, "month").format("MMMM");
  const navigate = useNavigate();

  useIdleCursor();
  useSessionGuard();
  useMonthRollover(month, year);
  // Eenmalige meting die zo nodig de kwaliteitstrap verlaagt.
  useFrameWatch();
  const { data, isReady, refresh } = useDashboardData(month, year);
  const rankView = useRankView(data.rankList.length);

  const { active, playGroup, playParty, rootClass, stopAll } = useAnimations({
    keyboard: testShortcuts,
  });
  useSaleEvents(isReady ? data : null, playGroup, `${month}-${year}`);
  // Ranglijsten wisselen pas om als de stijger-animatie in beeld komt.
  const ranking = useHeldRanking(data, active);

  // Verborgen codes: "ja" opent het animatielab, "feest" start het feestje.
  useSecretCode({
    ja: () => {
      unlockLab();
      navigate(`/animaties/${month}/${year}`);
    },
    feest: playParty,
  });

  // Tv uit: animaties stoppen; tv aan: meteen ophalen voor "Verkopen gemist".
  useScreenAwake({ onWake: refresh, onDark: stopAll });

  if (!isReady) {
    return (
      <ScaledCanvas>
        <div className="dashboard-container">
          <Navbar />
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      </ScaledCanvas>
    );
  }

  const progress = targetProgress({
    scored: data.scoredTowardsTarget,
    target: data.teamTarget.amount,
    month,
    year,
  });
  const monthLabel = monthName.toUpperCase();

  return (
    <ScaledCanvas>
      <div className={`dashboard-container ${rootClass}`}>
        <Navbar />
        <TopbarClock />
        <AnimationLayer active={active} />

        <div className="dash-body">
          <TeamTargetHero
            monthLabel={monthLabel}
            teamTarget={data.teamTarget}
            amount={data.amount}
            progress={progress}
          />

          <section className="dash-cols">
            <MostSoldServices
              soldServices={data.soldServices}
              monthLabel={monthLabel}
            />
            <MonthlyRankList
              rankList={ranking.rankList}
              rankView={rankView}
              now={monthName}
              bestSeller={data.bestSeller}
              previousMonth={previousMonthName}
              deals={data.deals}
              existantDeals={data.existantDeals}
            />
            <YearRankList yearRankList={ranking.yearRankList} />
          </section>
        </div>
      </div>
    </ScaledCanvas>
  );
};

export default Dashboard;
